import { useRouter } from 'next/compat/router';
import useProductQueryParams from 'hooks/use-product-query-params';
import usePaths from 'hooks/use-paths';
import { getCustomCategoryLink } from 'src/dispensary/hooks/use-get-custom-category-link';
import _ from 'lodash';
import { GenericCategory } from 'src/generics/data';

type UseCategorySubcategoryFilterReturn = {
  selectedCategory: GenericCategory;
  selectedSubcategories: string[] | [];
  handleClickCategory: (categoryValue: string | null) => void;
  handleClickSubcategoryMultiSelect: (subcategoryValue: string | null) => void;
};

type UseCategorySubcategoryFilterOptions = {
  /**
   * Category pages (/stores/[cName]/products/[category]) are path-routed, not
   * query-param-routed like search/collections/brands. When true, clicking a category
   * navigates via router.push to the category's URL instead of updating the `category`
   * query param -- see PF-9862.
   */
  navigateOnCategoryClick?: boolean;
};

export function useCategorySubcategoryFilter({
  navigateOnCategoryClick = false,
}: UseCategorySubcategoryFilterOptions = {}): UseCategorySubcategoryFilterReturn {
  const { defaults, queryParams, setQueryParams } = useProductQueryParams();
  const router = useRouter();
  const { href: baseHref } = usePaths();
  // Category pages (/products/[category]) carry the active category in the URL path, not the
  // `category` query param used by search/collections/brands -- fall back to the router's path
  // param so the active category is highlighted correctly in this rendering mode. See PF-9862.
  const selectedCategory = navigateOnCategoryClick
    ? (router?.query?.category as string | undefined) ?? queryParams.category
    : queryParams.category;
  const selectedSubcategories = queryParams.subcategories;

  const handleClickSubcategoryMultiSelect = (subcategoryValue: string | null): void => {
    const newSubcategories = _.xor(selectedSubcategories, [subcategoryValue]);
    setQueryParams({ subcategories: newSubcategories, page: defaults.page });
  };

  const handleClickCategory = (categoryValue: string | null): void => {
    if (navigateOnCategoryClick) {
      // A null categoryValue means "All Categories" -- go back to the dispensary's menu
      // home (no category filter) rather than the standalone /categories browse page.
      const href = categoryValue ? getCustomCategoryLink(baseHref, false, undefined, categoryValue) : baseHref;
      if (href) {
        void router?.push(href);
      }
      return;
    }

    setQueryParams({
      category: categoryValue ?? defaults.category,
      subcategories: defaults.subcategories,
      page: defaults.page,
    });
  };

  return {
    selectedCategory,
    selectedSubcategories,
    handleClickCategory,
    handleClickSubcategoryMultiSelect,
  };
}
